import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const RegistrationModal = ({ isOpen, onClose, courseName }) => {
  const { t, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    education: '',
    educationOther: '',
    course: courseName || '',
    comments: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  // Update course name when courseName prop changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      course: courseName || ''
    }));
  }, [courseName]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Prepare education field - if "other" is selected, use the custom text
      const educationValue = formData.education === 'other' 
        ? formData.educationOther 
        : formData.education;
      
      const response = await fetch(`${API_URL}/api/course-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          education: educationValue
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          education: '',
          educationOther: '',
          course: courseName || '',
          comments: ''
        });
        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting registration:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto ${isRTL ? 'text-right' : ''}`}>
        {/* Header */}
        <div className="bg-cyan-500 text-white p-6 rounded-t-lg relative">
          <button
            onClick={onClose}
            className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} text-white hover:text-slate-200 transition-colors`}
          >
            <X size={24} />
          </button>
          <h2 className="text-2xl font-bold pr-8">{t.cme.registrationForm}</h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Full Name */}
          <div className="mb-4">
            <label className={`block text-slate-700 font-semibold mb-2 ${isRTL ? 'text-right' : ''}`}>
              {t.cme.fullName} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${isRTL ? 'text-right' : ''}`}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className={`block text-slate-700 font-semibold mb-2 ${isRTL ? 'text-right' : ''}`}>
              {t.cme.emailAddress} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              dir="ltr"
            />
          </div>

          {/* Contact Number */}
          <div className="mb-4">
            <label className={`block text-slate-700 font-semibold mb-2 ${isRTL ? 'text-right' : ''}`}>
              {t.cme.contactNumber} <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              dir="ltr"
            />
          </div>

          {/* Level of Education */}
          <div className="mb-4">
            <label className={`block text-slate-700 font-semibold mb-2 ${isRTL ? 'text-right' : ''}`}>
              {t.cme.educationLevel} <span className="text-red-500">*</span>
            </label>
            <select
              name="education"
              value={formData.education}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${isRTL ? 'text-right' : ''}`}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <option value="">{t.cme.selectEducation}</option>
              <option value="highschool">{t.cme.highSchool}</option>
              <option value="diploma">{t.cme.diploma}</option>
              <option value="bachelor">{t.cme.bachelor}</option>
              <option value="master">{t.cme.master}</option>
              <option value="phd">{t.cme.phd}</option>
              <option value="other">{t.cme.other}</option>
            </select>
          </div>

          {/* Education Other - Show only if "Other" is selected */}
          {formData.education === 'other' && (
            <div className="mb-4">
              <label className={`block text-slate-700 font-semibold mb-2 ${isRTL ? 'text-right' : ''}`}>
                {t.cme.specifyEducation} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="educationOther"
                value={formData.educationOther}
                onChange={handleChange}
                required
                placeholder={t.cme.specifyEducation}
                className={`w-full px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${isRTL ? 'text-right' : ''}`}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>
          )}

          {/* Requested Course - Display Only (Not an input field) */}
          <div className="mb-6 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 border-l-4 border-cyan-500 rounded-md">
            <p className={`text-sm text-slate-600 mb-2 ${isRTL ? 'text-right' : ''}`}>
              {t.cme.requestedCourse}
            </p>
            <h3 className={`text-xl font-bold text-cyan-700 ${isRTL ? 'text-right' : ''}`}>
              {formData.course}
            </h3>
          </div>

          {/* Additional Comments */}
          <div className="mb-6">
            <label className={`block text-slate-700 font-semibold mb-2 ${isRTL ? 'text-right' : ''}`}>
              {t.cme.additionalComments}
            </label>
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              rows="4"
              placeholder={t.cme.commentsPlaceholder}
              className={`w-full px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none ${isRTL ? 'text-right' : ''}`}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>

          {/* Info Message */}
          <div className="mb-6 p-4 bg-cyan-50 border border-cyan-200 rounded-md">
            <p className={`text-slate-700 text-sm ${isRTL ? 'text-right' : ''}`}>
              {t.cme.teamWillContact}
            </p>
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <p className={`text-green-700 font-semibold ${isRTL ? 'text-right' : ''}`}>
                ✓ {t.cme.requestSent}
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className={`text-red-700 font-semibold ${isRTL ? 'text-right' : ''}`}>
                ✗ {t.cme.requestError}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {isRTL ? 'جاري الإرسال...' : 'Sending...'}
              </span>
            ) : (
              `🎓 ${t.cme.submitRequest}`
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RegistrationModal;
